<template>
    <v-content>
        <v-toolbar class="content-toolbar">
            <v-btn flat class="back-button" v-on:click="$router.go(-1)">
                <font-awesome-icon :icon="['far', 'chevron-left']" size="2x"/>
            </v-btn>
            <font-awesome-icon v-if="isEditMode" :icon="['fal', 'edit']" size="2x"/>
            <font-awesome-icon v-else :icon="['fal', 'pencil-alt']" size="2x"/>
            <v-toolbar-title>{{title}}</v-toolbar-title>

        </v-toolbar>

        <div class="content-container">
            <div class="form-container">
                <v-container fill-height class="fields-container">
                    <v-layout>
                        <v-flex>
                            <v-progress-linear v-if="isLoading" indeterminate color="secondary"></v-progress-linear>
                            <template v-else>
                                <div v-if="contentType">
                                    <v-form ref="formRef">
                                        <div v-for="datum in mappedNonServiceFields"
                                             v-bind:data="datum.field"
                                             v-bind:key="datum.field.identifier">
                                            <template v-if="datum.formModelFieldRef">
                                                <div v-if="datum.isTypeShortText" class="field-container-1">
                                                    <sfr-text-short-field :field="datum.field"
                                                                          :valid="datum.formModelFieldRef.valid"
                                                                          v-on:sfr:validation-state="datum.onValidationStateChange"
                                                                          v-on:sfr:touched="touchState"
                                                                          v-model="formModel[datum.field.identifier].value">
                                                    </sfr-text-short-field>
                                                </div>
                                                <div v-else-if="datum.isTypeLongText"
                                                     class="field-container-1">
                                                    <sfr-text-long-field :field="datum.field"
                                                                         :valid="datum.formModelFieldRef.valid"
                                                                         v-on:sfr:validation-state="datum.onValidationStateChange"
                                                                         v-on:sfr:touched="touchState"
                                                                         v-model="datum.formModelFieldRef.value">
                                                    </sfr-text-long-field>
                                                </div>
                                                <div v-else-if="datum.isTypeBoolean"
                                                     class="field-container-1">
                                                    <sfr-boolean-field :field="datum.field"
                                                                       v-on:sfr:touched="touchState"
                                                                       v-model="datum.formModelFieldRef.value">
                                                    </sfr-boolean-field>
                                                </div>
                                                <div v-else-if="datum.isTypeNumber"
                                                     class="field-container-1">
                                                    <sfr-number-field :field="datum.field"
                                                                      :valid="datum.formModelFieldRef.valid"
                                                                      v-on:sfr:validation-state="datum.onValidationStateChange"
                                                                      v-on:sfr:touched="touchState"
                                                                      v-model="datum.formModelFieldRef.value">
                                                    </sfr-number-field>
                                                </div>
                                                <div v-else-if="datum.isTypeDatetime"
                                                     class="field-container-1">
                                                    <sfr-datetime-field :field="datum.field"
                                                                        :valid="datum.formModelFieldRef.valid"
                                                                        v-on:sfr:validation-state="datum.onValidationStateChange"
                                                                        v-on:sfr:touched="touchState"
                                                                        v-model="datum.formModelFieldRef.value">
                                                    </sfr-datetime-field>
                                                </div>
                                                <div v-else-if="datum.isTypeLocation"
                                                     class="field-container-1">
                                                    <sfr-location-field :field="datum.field"
                                                                        :valid="datum.formModelFieldRef.valid"
                                                                        v-on:sfr:validation-state="datum.onValidationStateChange"
                                                                        v-on:sfr:touched="touchState"
                                                                        v-model="datum.formModelFieldRef.value">
                                                    </sfr-location-field>

                                                </div>
                                                <div v-else-if="datum.isTypeReference"
                                                     class="field-container-1">
                                                    <sfr-reference-field :field="datum.field"
                                                                         :valid="datum.formModelFieldRef.valid"
                                                                         v-on:sfr:validation-state="datum.onValidationStateChange"
                                                                         v-on:sfr:touched="touchState"
                                                                         v-on:sfr:nested-creation="pushNestedCreationObjectAndNavigate"
                                                                         v-model="datum.formModelFieldRef.value">
                                                    </sfr-reference-field>
                                                </div>
                                            </template>
                                        </div>
                                    </v-form>
                                </div>
                            </template>
                        </v-flex>
                    </v-layout>
                </v-container>
            </div>

            <div class="crud-sidebar-container">
                <v-navigation-drawer permanent class="crud-sidebar">
                    <div>
                        <template v-if="isEditMode">
                            <v-subheader>Status</v-subheader>
                            <v-divider></v-divider>
                            <div v-if="isFormPopulated" class="right-side-bar">
                        <span v-if="status"><font-awesome-icon :icon="['fas', 'circle']" class="icon-status"
                                                               v-bind:class="statusCssString"/></span>
                                <span>Status: </span>
                                <span class="capitalize-text">{{status}}</span>
                                <div>
                                    <v-btn v-if="isStatusNotPublished" block color="success"
                                           v-on:click="onPublishButtonClick" :loading="isStatusPublishLoading"
                                           :disabled="isPublishButtonDisabled">
                                        <span class="capitalize-text">publish</span>
                                    </v-btn>
                                    <v-btn v-if="isStatusNotDraft" block color="warning" v-on:click="onDraftButtonClick"
                                           :loading="isStatusDraftLoading" :disabled="isDraftButtonDisabled">
                                        <span class="capitalize-text">revert to draft</span>
                                    </v-btn>
                                    <v-btn v-if="isStatusNotArchive" block color="error"
                                           v-on:click="onArchiveButtonClick" :loading="isStatusArchiveLoading"
                                           :disabled="isArchiveButtonDisabled">
                                        <span class="capitalize-text">archive</span>
                                    </v-btn>
                                </div>
                            </div>
                        </template>
                        <template>
                            <v-subheader>Actions</v-subheader>
                            <v-divider></v-divider>
                            <div class="right-side-bar">
                                <div>
                                    <v-btn v-if="isEditMode" block color="info" v-on:click="updateEntry()"
                                           :loading="isUpdateLoadingStateLoading" :disabled="isUpdateButtonDisabled">
                                        <span class="capitalize-text">Update</span>
                                    </v-btn>
                                    <template v-else>
                                        <v-btn block color="info"
                                               v-on:click="addEntry()"
                                               :loading="isAddLoadingStateLoading"
                                               :disabled="isAddButtonDisabled">
                                            <span class="capitalize-text">Save as Draft</span>
                                        </v-btn>
                                        <v-btn block color="info" v-on:click="onAddPublishButtonClick"
                                               :loading="isAddPublishLoadingStateLoading"
                                               :disabled="isAddPublishButtonDisabled">
                                            <span class="capitalize-text">Publish</span>
                                        </v-btn>
                                    </template>
                                </div>
                            </div>
                        </template>
                        <template v-if="isEditMode && localizations">
                            <v-subheader class="localization-section-subheader">Localization</v-subheader>
                            <v-divider></v-divider>
                            <div class="right-side-bar">
                                <span>Current: </span>
                                <span class="capitalize-text">{{languageDisplay(currentLocale)}}</span>
                                <div class="language-button-toggle">
                                    <v-btn-toggle v-model="currentLocaleIndex" mandatory>
                                        <v-btn v-for="locale in localizations" v-bind:key="locale" flat
                                               v-on:click="navigateToLocale(locale)">
                                            {{languageDisplay(locale)}}
                                        </v-btn>
                                    </v-btn-toggle>
                                </div>
                            </div>
                        </template>
                    </div>

                </v-navigation-drawer>
            </div>
        </div>
        <v-snackbar
                :timeout="snackBar.timeout"
                :bottom="true"
                v-model="snackBar.isShowing"
                :color="snackBar.color">
            {{ snackBar.text }}
            <v-btn dark flat @click.native="snackBar.isShowing = false">Ok</v-btn>
        </v-snackbar>
    </v-content>

</template>

<script>

    import http from '../services/axios'
    import lodash from '../services/lodash'

    import {
        TYPE_TEXT_SHORT,
        TYPE_TEXT_LONG,
        TYPE_BOOLEAN,
        TYPE_NUMBER,
        TYPE_DATETIME,
        TYPE_LOCATION,
        TYPE_REFERENCE,
        CONTENT_TYPE_ASSET,
        LOAD_SPACE_CONTENT,
        LOADING_COMPLETED_FAILURE,
        LOADING_COMPLETED_SUCCESS,
        LOADING_PRISTINE,
        LOADING_STARTED,
        DRAFT_TEXT,
        PUBLISHED_TEXT,
        ARCHIVED_TEXT,
        PUSH_NESTED_CREATION_OBJECT,
        GET_LOCALE_DISPLAY_NAME,
        GET_NESTED_CREATION_STACK_TOP,
        REMOVE_NESTED_CREATION_STACK_TOP,
        SET_REFERENCE_NESTED_CREATION_OBJECT_STACK_TOP,
        SET_CURRENT_LOCALE,
        R_NAME_EDIT_MEDIA,
        R_NAME_ADD_MEDIA,
        R_NAME_ADD_ENTRY,
        R_NAME_EDIT_ENTRY,
        R_NAME_EDIT_ENTRY_LOCALE,
        SNACKBAR_COLOR_SUCCESS,
        SNACKBAR_COLOR_INFO,
        SNACKBAR_COLOR_ERROR,
        DEFAULT_LOCALE,
    } from "../services/constants"
    import helpers from '../services/helpers'

    export default {
        name: 'space-dashboard-crud-entry',
        data: function () {
            return {
                status: DRAFT_TEXT,
                formModel: {},
                hiddenFormModel: {},
                isFormValid: false,
                isFormDirty: false,
                isFormPopulated: false,
                rawLoadingState: {
                    state: LOADING_PRISTINE
                },
                updateLoadingState: {
                    state: LOADING_PRISTINE,
                    name: 'Upload'
                },
                publishLoadingState: {
                    state: LOADING_PRISTINE,
                    name: 'Publish'
                },
                draftLoadingState: {
                    state: LOADING_PRISTINE,
                    name: 'Draft'
                },
                archiveLoadingState: {
                    state: LOADING_PRISTINE,
                    name: 'Archive'
                },
                addLoadingState: {
                    state: LOADING_PRISTINE
                },
                addPublishLoadingState: {
                    state: LOADING_PRISTINE
                },
                snackBar: {
                    isShowing: false,
                    text: '',
                    color: 'info',
                    timeout: 6000
                }
            }
        },
        computed: {
            contentType() {
                let contentTypeOfRoute = null
                if (this.$store.state.spaceContentTypes) {
                    this.$store.state.spaceContentTypes.forEach(contentType => {
                        if (contentType.identifier === this.$route.params.contentTypeIdentifier)
                            contentTypeOfRoute = contentType
                    })
                }
                return contentTypeOfRoute
            },
            localizations() {
                return this.$store.state.locale.spaceLocalizations
            },
            currentLocale() {
                return this.$store.state.locale.currentLocale
            },
            currentLocaleIndex: {
                get: function () {
                    return this.$store.state.locale.currentLocaleIndex
                },
                set: function (newValue) {

                }
            },
            title() {
                const identifier = this.getPrimaryDisplayIdentifier(this.contentType)
                let title = ''
                if (this.contentType) {
                    title += this.contentType.name
                    title += ' > '
                }
                if (this.formModel[identifier] && this.formModel[identifier].value) {
                    title += this.formModel[identifier].value
                } else {
                    title += 'Untitled'
                }
                if (this.isFormDirty) {
                    title += '*'
                }
                return title
            },
            nonServiceFields() {
                return this.contentType.fields.filter(helpers.excludeFieldsWithServiceValidation)
            },
            mappedNonServiceFields() {
                return this.nonServiceFields.map(field => {
                    const formModelFieldRef = this.formModel[field.identifier]
                    const isTypeShortText = field.type === TYPE_TEXT_SHORT
                    const isTypeLongText = field.type === TYPE_TEXT_LONG
                    const isTypeBoolean = field.type === TYPE_BOOLEAN
                    const isTypeNumber = field.type === TYPE_NUMBER
                    const isTypeDatetime = field.type === TYPE_DATETIME
                    const isTypeLocation = field.type === TYPE_LOCATION
                    const isTypeReference = field.type === TYPE_REFERENCE

                    let isFieldValid = false
                    let onValidationStateChange = () => {

                    }
                    if (formModelFieldRef) {
                        isFieldValid = formModelFieldRef.valid
                        onValidationStateChange = (newState) => {
                            formModelFieldRef.valid = newState
                        }
                    }

                    return {
                        field,
                        isTypeShortText,
                        isTypeLongText,
                        isTypeBoolean,
                        isTypeNumber,
                        isTypeDatetime,
                        isTypeLocation,
                        isTypeReference,
                        isFieldValid,
                        onValidationStateChange,
                        formModelFieldRef,
                    }
                })
            },
            isEditMode() {
                return this.$route.name === R_NAME_EDIT_ENTRY
                    || this.$route.name === R_NAME_EDIT_ENTRY_LOCALE
            },
            computedLoadingState() {
                if (this.isEditMode) {
                    return this.rawLoadingState.state
                } else {
                    return LOADING_COMPLETED_SUCCESS
                }
            },
            isLoading() {
                return this.computedLoadingState === LOADING_STARTED
            },
            isEntireFormValid() {
                return !Object.keys(this.formModel).map(key => {
                    return this.formModel[key].valid
                }).some(validationResult => {
                    return !validationResult
                })
            },
            statusCssString() {
                return helpers.cssComputeStatusColor(this.status)
            },
            isStatusNotPublished() {
                return this.status !== PUBLISHED_TEXT
            },
            isStatusPublishLoading() {
                return this.publishLoadingState.state === LOADING_STARTED
            },
            isPublishButtonDisabled() {
                return this.publishLoadingState.state === LOADING_STARTED || !this.isEntireFormValid
            },
            isStatusNotDraft() {
                return this.status !== DRAFT_TEXT
            },
            isStatusDraftLoading() {
                return this.draftLoadingState.state === LOADING_STARTED
            },
            isDraftButtonDisabled() {
                return this.draftLoadingState.state === LOADING_STARTED || !this.isEntireFormValid
            },
            isStatusNotArchive() {
                return this.status !== ARCHIVED_TEXT
            },
            isStatusArchiveLoading() {
                return this.archiveLoadingState.state === LOADING_STARTED
            },
            isArchiveButtonDisabled() {
                return this.archiveLoadingState.state === LOADING_STARTED || !this.isEntireFormValid
            },
            isUpdateLoadingStateLoading() {
                return this.updateLoadingState.state === LOADING_STARTED
            },
            isUpdateButtonDisabled() {
                return this.updateLoadingState.state === LOADING_STARTED || !this.isEntireFormValid || !this.isFormDirty
            },
            isUpdateProgressCompleted() {
                return this.updateLoadingState.progress !== 100
            },
            isAddLoadingStateLoading() {
                return this.addLoadingState.state === LOADING_STARTED
            },
            isAddButtonDisabled() {
                return this.addLoadingState.state === LOADING_STARTED || !this.isEntireFormValid
            },
            isAddProgressCompleted() {
                return this.addLoadingState.progress !== 100
            },
            isAddPublishLoadingStateLoading() {
                return this.addPublishLoadingState.state === LOADING_STARTED
            },
            isAddPublishButtonDisabled() {
                return this.addPublishLoadingState.state === LOADING_STARTED || !this.isEntireFormValid
            },
            isAddPublishProgressCompleted() {
                return this.addPublishLoadingState.progress !== 100
            },
        },
        watch: {
            contentType() {
                this.init()
            },
            'currentLocale'() {
                this.currentLocaleIndex = this.$store.state.locale.currentLocaleIndex
            }
        },
        methods: {
            buildFormModel() {
                if (this.contentType) {
                    let savedState = null
                    if (this.$store.state.nestedCreateObjectStack.length > 0) {
                        const topState = this.$store.getters[GET_NESTED_CREATION_STACK_TOP]
                        if (topState.routeSnapshot.fullPath === this.$route.fullPath) {
                            this.$store.commit(REMOVE_NESTED_CREATION_STACK_TOP)
                            savedState = topState.formModel
                            this.status = topState.status
                        }
                    }
                    this.cleanFormModel()

                    this.nonServiceFields.forEach(field => {
                        if (!this.formModel[field.identifier]) {
                            this.$set(this.formModel, field.identifier, {})
                            switch (field.type) {
                                case TYPE_TEXT_SHORT:
                                    this.$set(this.formModel[field.identifier], 'value', savedState ?
                                        savedState[field.identifier].value : field.allowsMultiple ? [''] : '')
                                    this.$set(this.formModel[field.identifier], 'valid', savedState ?
                                        savedState[field.identifier].valid : false)
                                    break
                                case TYPE_TEXT_LONG:
                                    this.$set(this.formModel[field.identifier], 'value', savedState ?
                                        savedState[field.identifier].value : '')
                                    this.$set(this.formModel[field.identifier], 'valid', savedState ?
                                        savedState[field.identifier].valid : false)
                                    break
                                case TYPE_BOOLEAN:
                                    this.$set(this.formModel[field.identifier], 'value', savedState ?
                                        savedState[field.identifier].value : false)
                                    this.$set(this.formModel[field.identifier], 'valid', true)
                                    break
                                default:
                                    this.$set(this.formModel[field.identifier], 'value', savedState ?
                                        savedState[field.identifier].value : field.allowsMultiple ? [] : null)
                                    this.$set(this.formModel[field.identifier], 'valid', savedState ?
                                        savedState[field.identifier].valid : false)
                                    break
                            }
                        }
                    })
                    if (savedState === null) {
                        this.populateFormModel()
                    } else {
                        this.isFormPopulated = true
                        this.touchState()
                    }
                }

            },
            cleanFormModel() {
                for (const key of Object.keys(this.formModel)) {
                    this.$delete(this.formModel[key], 'value')
                    this.$delete(this.formModel[key], 'valid')
                    this.$delete(this.formModel, key)
                }
            },
            populateFormModel() {
                if (this.isEditMode) {
                    this.rawLoadingState.state = LOADING_STARTED
                    return http.get(`api/management/spaces/${this.$store.state.spaceMetadata.id}/entries/${this.currentLocale === DEFAULT_LOCALE ? '' : `${this.currentLocale}/`}${this.$route.params.contentTypeIdentifier}/${this.$route.params.id}`)
                        .then(results => {
                            const incomingObj = helpers.buildIncomingObj(results.data)
                            this.rawLoadingState.state = LOADING_COMPLETED_SUCCESS
                            this.status = incomingObj.status
                            for (const key of Object.keys(incomingObj.fields)) {
                                const obj = this.formModel[results.data.fields[key].identifier]

                                //This if statement determines whether or not this field should be in the hidden form model or not.
                                // Functionally, obj being truey indicates that it is a regular field that exists on the regular form model,
                                // and therefore should be populated. Otherwise it should go in a new key in the hidden form model
                                if (obj) {
                                    obj.value = helpers.castField(incomingObj.fields[key])
                                } else {
                                    this.hiddenFormModel[results.data.fields[key].identifier] = {
                                        value: helpers.castField(incomingObj.fields[key])
                                    }
                                }
                            }
                            this.isFormPopulated = true
                        }).catch(err => {
                            this.rawLoadingState.state = LOADING_COMPLETED_FAILURE
                            console.log(err)
                        })
                }
            },
            getPrimaryDisplayIdentifier: helpers.getPrimaryDisplayIdentifier,
            touchState: function () {
                this.isFormDirty = true
            },
            toggleStatusState(intendedStatusString) {
                const exitObj = {}
                exitObj['status'] = intendedStatusString

                let loadingStateReference = null;
                switch (intendedStatusString) {
                    case PUBLISHED_TEXT:
                        loadingStateReference = this.publishLoadingState
                        break
                    case DRAFT_TEXT:
                        loadingStateReference = this.draftLoadingState
                        break
                    case ARCHIVED_TEXT:
                        loadingStateReference = this.archiveLoadingState
                        break
                    default:
                        loadingStateReference = this.publishLoadingState
                        break
                }
                this.executeUpdate(loadingStateReference, exitObj)
            },
            updateEntry() {
                const exitObj = {}
                exitObj['status'] = this.status
                this.executeUpdate(this.updateLoadingState, exitObj)
            },
            executeUpdate(loadingStateReference, exitObj) {
                exitObj['id'] = this.$route.params.id
                exitObj['fields'] = []
                const mergedFormModel = Object.assign({}, this.formModel, this.hiddenFormModel)
                for (const key of Object.keys(mergedFormModel)) {
                    const field = {}
                    field['identifier'] = key
                    field['value'] = mergedFormModel[key].value
                    exitObj['fields'].push(field)
                }

                const flatExitObj = helpers.buildOutgoingObj(exitObj)
                loadingStateReference.state = LOADING_STARTED
                return http.post(`api/management/spaces/${this.$store.state.spaceMetadata.id}/entries/${this.currentLocale === DEFAULT_LOCALE ? '' : `${this.currentLocale}/`}${this.$route.params.contentTypeIdentifier}/${this.$route.params.id}`, flatExitObj)
                    .then(results => {
                        this.status = results.data.status
                        loadingStateReference.state = LOADING_COMPLETED_SUCCESS
                        this.isFormDirty = false
                        this.showSnackBar(`${loadingStateReference.name} complete.`, LOADING_COMPLETED_SUCCESS)

                        this.$store.dispatch(LOAD_SPACE_CONTENT, {
                            identifier: this.$route.params.contentTypeIdentifier
                        }).then(results2 => {
                        })
                    }).catch(err => {
                        loadingStateReference.state = LOADING_COMPLETED_FAILURE
                        this.showSnackBar(`${loadingStateReference.name} failed.`, LOADING_COMPLETED_FAILURE)
                        console.log(err)
                    })

            },
            onAddPublishButtonClick() {
                this.addEntry(PUBLISHED_TEXT)
            },
            addEntry(optionalStatus) {
                let loadingStateReference = null;
                const exitObj = {}
                if (optionalStatus) {
                    exitObj['status'] = optionalStatus
                    loadingStateReference = this.addPublishLoadingState
                } else {
                    loadingStateReference = this.addLoadingState
                }
                exitObj['fields'] = []
                for (const key of Object.keys(this.formModel)) {
                    const field = {}
                    field['identifier'] = key
                    field['value'] = this.formModel[key].value
                    exitObj['fields'].push(field)
                }
                const flatExitObj = helpers.buildOutgoingObj(exitObj)

                loadingStateReference.state = LOADING_STARTED
                return http.post(`api/management/spaces/${this.$store.state.spaceMetadata.id}/entries/${this.$route.params.contentTypeIdentifier}`, flatExitObj)
                    .then(results => {
                        this.$store.dispatch(LOAD_SPACE_CONTENT, {
                            identifier: this.$route.params.contentTypeIdentifier
                        }).then(results2 => {
                            loadingStateReference.state = LOADING_COMPLETED_SUCCESS
                            this.isFormDirty = false
                            this.isFormPopulated = true
                            this.showSnackBar('Entry added.', LOADING_COMPLETED_SUCCESS)
                            if (this.$store.state.nestedCreateObjectStack.length === 0) {
                                this.$router.replace(
                                    {
                                        name: R_NAME_EDIT_ENTRY,
                                        params: {
                                            contentTypeIdentifier: this.$route.params.contentTypeIdentifier,
                                            id: results.data.id,
                                        }
                                    })
                            } else {
                                this.$store.commit(SET_REFERENCE_NESTED_CREATION_OBJECT_STACK_TOP, results.data)
                                const topOfStack = this.$store.getters[GET_NESTED_CREATION_STACK_TOP]
                                this.$router.replace(
                                    {
                                        name: topOfStack.routeSnapshot.name,
                                        params: topOfStack.routeSnapshot.params
                                    })
                            }

                        })
                    }).catch(err => {
                        loadingStateReference.state = LOADING_COMPLETED_FAILURE
                        this.showSnackBar('Adding entry failed.', LOADING_COMPLETED_FAILURE)
                        console.log(err)
                    })

            },
            pushNestedCreationObjectAndNavigate(field) {
                this.$store.commit(PUSH_NESTED_CREATION_OBJECT, {
                    formModel: this.formModel,
                    routeSnapshot: lodash.pick(['fullPath', 'name', 'params', 'path'], this.$route),
                    contentTypeIdentifier: field.referenceType,
                    selectedField: field.identifier,
                    status: this.status,
                })
                if (field.referenceType !== CONTENT_TYPE_ASSET) {
                    this.$router.replace({
                        name: R_NAME_ADD_ENTRY,
                        params: {contentTypeIdentifier: field.referenceType}
                    })
                } else {
                    this.$router.replace({
                        name: R_NAME_ADD_MEDIA,
                    })
                }
            },
            navigateToLocale(locale) {
                if (locale === DEFAULT_LOCALE) {
                    const newParams = lodash.cloneDeep(this.$route.params)
                    delete newParams.locale
                    this.$router.replace({
                        name: R_NAME_EDIT_ENTRY,
                        params: newParams,
                    })
                } else {
                    const newParams = lodash.merge({locale: locale}, this.$route.params)
                    this.$router.replace({
                        name: R_NAME_EDIT_ENTRY_LOCALE,
                        params: newParams,
                    })
                }
            },
            showSnackBar(text, status) {
                this.snackBar.text = text
                switch (status) {
                    case LOADING_COMPLETED_SUCCESS:
                        this.snackBar.color = SNACKBAR_COLOR_SUCCESS
                        break
                    case LOADING_COMPLETED_FAILURE:
                        this.snackBar.color = SNACKBAR_COLOR_ERROR
                        break
                    default:
                        this.snackBar.color = SNACKBAR_COLOR_INFO
                        break
                }
                this.snackBar.isShowing = true
            },
            onPublishButtonClick() {
                this.toggleStatusState(PUBLISHED_TEXT)
            },
            onDraftButtonClick() {
                this.toggleStatusState(DRAFT_TEXT)
            },
            onArchiveButtonClick() {
                this.toggleStatusState(ARCHIVED_TEXT)
            },
            languageDisplay(locale) {
                const foundLanguage = this.$store.getters[GET_LOCALE_DISPLAY_NAME](locale)
                return foundLanguage ? foundLanguage : ''
            },
            init() {
                const to = this.$route
                if (to.name === R_NAME_EDIT_ENTRY) {
                    this.$store.commit(SET_CURRENT_LOCALE, DEFAULT_LOCALE)
                } else if (to.name === R_NAME_EDIT_ENTRY_LOCALE) {
                    this.$store.commit(SET_CURRENT_LOCALE, to.params['locale'])
                }
                this.buildFormModel()
            }
        },
        filters: {
            uppercase(value) {
                return value.toUpperCase()
            }
        },
        created() {
            if (this.contentType) {
                this.init()
            }
        },

    }
</script>

<style scoped lang="scss">
    @import "../meta";

    $offset-height: 68px;
    .content-toolbar {
        position: fixed;
        margin-bottom: 4px;
        z-index: 4;

        .back-button {
            height: 100%;
            margin: 0 1em 0 0;
        }
    }

    .content-container {
        display: flex;
        margin: $offset-height 0 0 0;
        height: calc(100% - 68px);
        justify-content: center;

        .form-container {
            margin: 0 300px 0 0;
        }

        .fields-container {
            max-width: 1000px;
            min-width: 600px;
        }

        .crud-sidebar-container {
            height: 100%;
            position: fixed;
            right: 0;
        }

        .crud-sidebar {
            z-index: 3;
        }
    }

    .right-side-bar {
        padding: 1em;
    }

    .localization-section-subheader {
        margin: 5em 0 0 0;
    }

    .language-button-toggle {
        margin: .5em 0 0 0;
    }

    .icon-status {
        margin: 0 .75em 0 0;
    }

    .field-container-1 {
        margin: 2em 0;
    }
</style>
